/**
 * NTF-local formatting helpers. Absolute date/number formatting comes from
 * @teivor/shared-utils/format (Czech-first Intl); this adds the relative-time
 * presentation the notification feed + delivery log need, bilingual.
 */
import { formatDateTime, type Locale } from '@teivor/shared-utils';
import { L } from '@teivor/shared-utils/i18n';

/**
 * Compact bilingual relative time: "6 min", "1 h", "2 d", "3 týd / 3 wk".
 * Uses a fixed `now` arg so callers can keep it pure / testable.
 */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffMs = Math.max(0, now - then);
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return L('teď', 'now');
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} d`;
  const wk = Math.floor(day / 7);
  return `${wk} týd / ${wk} wk`;
}

/** Full, locale-formatted timestamp for tooltips/title attributes. */
export function fullTimestamp(iso: string, locale: Locale = 'cs-CZ'): string {
  return formatDateTime(iso, locale);
}
